import React, { useState } from 'react';
import { X, Bot, Settings, Target, BarChart3, Users, CheckCircle } from 'lucide-react';
import { SelectField } from "components/ui/select-field";

const AIConfigurationModal = ({ 
  isOpen, 
  onClose, 
  campaign, 
  aiAgent, 
  onSaveConfiguration 
}) => {
  const [config, setConfig] = useState({
    optimizationGoals: ['open_rate', 'click_rate'],
    automationLevel: 'moderate',
    testFrequency: 'weekly',
    confidenceThreshold: 95,
    enableAutoImplementation: false,
    enableContentGeneration: true,
    enableSubjectLineOptimization: true,
    enableSendTimeOptimization: false,
    enableAudienceSegmentation: true
  });

  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveConfiguration(campaign.id, config);
      onClose();
    } catch (error) {
      console.error('Error saving AI configuration:', error);
    } finally {
      setSaving(false);
    }
  };

  const optimizationGoalOptions = [
    { id: 'open_rate', label: 'Open Rate', icon: BarChart3, description: 'Optimize for email opens' },
    { id: 'click_rate', label: 'Click Rate', icon: Target, description: 'Optimize for link clicks' },
    { id: 'conversion_rate', label: 'Conversion Rate', icon: CheckCircle, description: 'Optimize for conversions' },
    { id: 'engagement', label: 'Engagement', icon: Users, description: 'Optimize for overall engagement' }
  ];

  const automationLevels = [
    { id: 'conservative', label: 'Conservative', description: 'Minimal automation, requires approval for most changes' },
    { id: 'moderate', label: 'Moderate', description: 'Balanced automation with key decisions requiring approval' },
    { id: 'aggressive', label: 'Aggressive', description: 'High automation, auto-implements most optimizations' }
  ];

  const testFrequencyOptions = [
    { id: 'daily', label: 'Daily', description: 'Run new tests daily' },
    { id: 'weekly', label: 'Weekly', description: 'Run new tests weekly' },
    { id: 'monthly', label: 'Monthly', description: 'Run new tests monthly' },
    { id: 'as_needed', label: 'As Needed', description: 'Run tests based on performance triggers' }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/75 backdrop-blur-[3px] flex items-center justify-center z-50 p-4">
      <div className="bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-2xl shadow-2xl dark:shadow-[0_20px_60px_rgba(0,0,0,0.5)] max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="h-[3px] bg-gradient-to-r from-violet-500 to-purple-500" />
        <div className="flex items-center justify-between px-6 py-4 border-b border-line-subtle dark:border-dark-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-50 dark:bg-violet-900/30 rounded-xl">
              <Bot className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-content dark:text-white">
                AI Configuration for {campaign.name}
              </h2>
              <p className="text-[12px] text-content-muted dark:text-gray-400">
                Configure AI agent behavior and optimization settings
              </p>
            </div>
          </div>
          <button aria-label="Close"
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-content-subtle dark:text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-5">
          {/* AI Agent Info */}
          {aiAgent && (
            <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800/50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Bot className="w-5 h-5 text-purple-600 dark:text-purple-300" />
                <span className="font-medium text-purple-900 dark:text-purple-300">{aiAgent.name}</span>
                <span className="text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
                  {aiAgent.specialization} Specialist
                </span>
              </div>
              <p className="text-sm text-purple-700 dark:text-purple-300">
                This AI agent will optimize your campaign based on the configuration below.
              </p>
            </div>
          )}

          {/* Optimization Goals */}
          <div>
            <h3 className="text-lg font-medium text-content mb-3">Optimization Goals</h3>
            <p className="text-sm text-content-secondary mb-4">
              Select which metrics the AI should focus on optimizing
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {optimizationGoalOptions.map(goal => {
                const Icon = goal.icon;
                return (
                  <label key={goal.id} className="flex items-center p-3 border border-line rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.optimizationGoals.includes(goal.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setConfig(prev => ({
                            ...prev,
                            optimizationGoals: [...prev.optimizationGoals, goal.id]
                          }));
                        } else {
                          setConfig(prev => ({
                            ...prev,
                            optimizationGoals: prev.optimizationGoals.filter(g => g !== goal.id)
                          }));
                        }
                      }}
                      className="rounded border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500 mr-3"
                    />
                    <Icon className="w-5 h-5 text-content-secondary mr-2" />
                    <div>
                      <div className="font-medium text-content">{goal.label}</div>
                      <div className="text-sm text-content-secondary">{goal.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Automation Level */}
          <div>
            <h3 className="text-lg font-medium text-content mb-3">Automation Level</h3>
            <p className="text-sm text-content-secondary mb-4">
              Choose how much autonomy the AI agent should have
            </p>
            <div className="space-y-3">
              {automationLevels.map(level => (
                <label key={level.id} className="flex items-start p-3 border border-line rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer">
                  <input
                    type="radio"
                    name="automationLevel"
                    value={level.id}
                    checked={config.automationLevel === level.id}
                    onChange={(e) => setConfig(prev => ({ ...prev, automationLevel: e.target.value }))}
                    className="rounded-full border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500 mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-content">{level.label}</div>
                    <div className="text-sm text-content-secondary">{level.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Test Frequency */}
          <div>
            <h3 className="text-lg font-medium text-content mb-3">Test Frequency</h3>
            <p className="text-sm text-content-secondary mb-4">
              How often should the AI create new A/B tests?
            </p>
            <SelectField
              value={config.testFrequency}
              onChange={(e) => setConfig(prev => ({ ...prev, testFrequency: e.target.value }))}
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {testFrequencyOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label} - {option.description}
                </option>
              ))}
            </SelectField>
          </div>

          {/* Confidence Threshold */}
          <div>
            <h3 className="text-lg font-medium text-content mb-3">Confidence Threshold</h3>
            <p className="text-sm text-content-secondary mb-4">
              Minimum confidence level required for AI decisions (higher = more conservative)
            </p>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="80"
                max="99"
                value={config.confidenceThreshold}
                onChange={(e) => setConfig(prev => ({ ...prev, confidenceThreshold: parseInt(e.target.value) }))}
                className="flex-1"
              />
              <span className="font-medium text-content min-w-[3rem]">
                {config.confidenceThreshold}%
              </span>
            </div>
          </div>

          {/* Feature Toggles */}
          <div>
            <h3 className="text-lg font-medium text-content mb-3">AI Features</h3>
            <p className="text-sm text-content-secondary mb-4">
              Enable or disable specific AI capabilities
            </p>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 border border-line rounded-lg">
                <div>
                  <div className="font-medium text-content">Auto-implement Winners</div>
                  <div className="text-sm text-content-secondary">Automatically implement winning A/B test variants</div>
                </div>
                <input
                  type="checkbox"
                  checked={config.enableAutoImplementation}
                  onChange={(e) => setConfig(prev => ({ ...prev, enableAutoImplementation: e.target.checked }))}
                  className="rounded border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-line rounded-lg">
                <div>
                  <div className="font-medium text-content">Content Generation</div>
                  <div className="text-sm text-content-secondary">Generate new content variations for testing</div>
                </div>
                <input
                  type="checkbox"
                  checked={config.enableContentGeneration}
                  onChange={(e) => setConfig(prev => ({ ...prev, enableContentGeneration: e.target.checked }))}
                  className="rounded border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-line rounded-lg">
                <div>
                  <div className="font-medium text-content">Subject Line Optimization</div>
                  <div className="text-sm text-content-secondary">Optimize email subject lines automatically</div>
                </div>
                <input
                  type="checkbox"
                  checked={config.enableSubjectLineOptimization}
                  onChange={(e) => setConfig(prev => ({ ...prev, enableSubjectLineOptimization: e.target.checked }))}
                  className="rounded border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-line rounded-lg">
                <div>
                  <div className="font-medium text-content">Send Time Optimization</div>
                  <div className="text-sm text-content-secondary">Optimize email send times for each recipient</div>
                </div>
                <input
                  type="checkbox"
                  checked={config.enableSendTimeOptimization}
                  onChange={(e) => setConfig(prev => ({ ...prev, enableSendTimeOptimization: e.target.checked }))}
                  className="rounded border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500"
                />
              </label>

              <label className="flex items-center justify-between p-3 border border-line rounded-lg">
                <div>
                  <div className="font-medium text-content">Audience Segmentation</div>
                  <div className="text-sm text-content-secondary">Automatically create and optimize audience segments</div>
                </div>
                <input
                  type="checkbox"
                  checked={config.enableAudienceSegmentation}
                  onChange={(e) => setConfig(prev => ({ ...prev, enableAudienceSegmentation: e.target.checked }))}
                  className="rounded border-line-strong text-purple-600 dark:text-purple-300 focus:ring-purple-500"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-line bg-gray-50 dark:bg-dark-700">
          <div className="text-sm text-content-secondary">
            Changes will take effect immediately and apply to future AI decisions
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Settings className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIConfigurationModal;